import mixpanel from 'mixpanel-browser'
import { get as lodashGet } from 'lodash'
import moment from 'moment'
import * as Sentry from '@sentry/vue'
import UserApi from '@/api/user'
import store from '@/vuex/store'
import config from '@/config'
import Util from '@/util'
import PresentationMixpanelTracker from '@/utils/mixpanel/presentation.mixpanel-tracker'
import cookie from '@/services/cookie'
import { localGetters } from '@/utils/abtest.utils'
import { PUBLIC_ROUTES } from '@/constant/routes'
import { pushToQueue } from '@/utils/deferredAction.queue'

const mixpanelAvailable = config.mixpanel && config.mixpanel.projectToken
let isInitialized = false
let isUserIdentified = false

/**
 * Queue mechanism for Mixpanel events to ensure Unleash feature flags
 * are initialized before tracking occurs.
 * This prevents loss of cohort data and ensures accurate tracking.
 */

const isAdmin = () => {
  return store.state.isAdmin || window.location.href.includes('admin=true') ||
    sessionStorage.getItem('isAdmin') === 'true'
}

const register = (props) => {
  try {
    if (mixpanelAvailable && !isAdmin() && isInitialized) {
      mixpanel.register(props)
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const unregister = (prop) => {
  try {
    if (mixpanelAvailable && isInitialized) {
      mixpanel.unregister(prop)
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}
/**
 * thread together the UTM and referrer of landing site and presenter app
 * 1. UTM params on presenter app url takes precedence
 *  . if no UTM params on presenter app url, use UTM params from landing site cookie:
 * 2. Referrer: if referrer == landing site, referrer from landing site
 *    Else do nothing
 */
const setUtmAndReferrerSuperProps = () => {
  const generalCookieName = 'aha_landing_site'
  const keepUtmSessionStorageKey = 'keepUtmPersistence'
  const urlParams = new URLSearchParams(window.location.search)

  // 1. Read incoming UTM params from the URL
  const utm_campaign = urlParams.get('utm_campaign')
  const utm_medium = urlParams.get('utm_medium')
  const utm_source = urlParams.get('utm_source')

  // 2. Read unified cookie
  const raw = cookie.getValue(generalCookieName)
  let generalParams = {}
  try {
    generalParams = raw ? JSON.parse(raw) : {}
  } catch (_) {
    generalParams = {}
  }

  // 3. UTM handling
  if (utm_campaign || utm_medium || utm_source) {
    // New UTM present in the URL → clear the cookie so Mixpanel sees the fresh params
    cookie.deleteValue(generalCookieName)
    sessionStorage.setItem(keepUtmSessionStorageKey, 'true')
  } else if (generalParams.utm_source || generalParams.utm_medium || generalParams.utm_campaign) {
    // No UTM in URL, but we have them in our cookie → register into Mixpanel
    register({
      utm_campaign: generalParams.utm_campaign,
      utm_medium: generalParams.utm_medium,
      utm_source: generalParams.utm_source,
    })
    // then clear the cookie so it doesn't get re‑sent, and remember we did it
    cookie.deleteValue(generalCookieName)
    sessionStorage.setItem(keepUtmSessionStorageKey, 'true')
  } else if (!sessionStorage.getItem(keepUtmSessionStorageKey)) {
    // by default Mixpanel persist utm campaign params for as long as its cookies last (typically 1 year)
    // we want to turn off this behavior
    // stop_utm_persistence: true is an option
    // but we will need to send a event in case utm params are in cookie not in url
    ['utm_campaign', 'utm_medium', 'utm_source']
      .forEach((key) => unregister(key))
  }

  // 4. Referrer handling
  // set referrer to Mixpanel context
  // only if the document.referrer is pointing to landing site
  // since we will be more interested in the referrer of the landing site
  // eslint-disable-next-line max-len
  const isFromLandingSite = generalParams.landingSite && document.referrer?.startsWith(generalParams.landingSite)
  const landingReferrer = generalParams.referrer && isFromLandingSite ? generalParams.referrer : null
  if (landingReferrer) {
    register({
      $referrer: landingReferrer,
      $referring_domain: new URL(landingReferrer).hostname,
    })
  }

  // 5. Session‑level referrer (first ever referrer this session)
  // store the first referrer in sessionStorage
  // to ignore navigation within the app workflow like login, checkout ...
  let session_referrer = sessionStorage.getItem('session_referrer')
  if (session_referrer === undefined || session_referrer === null) {
    session_referrer = landingReferrer || document.referrer || ''
    sessionStorage.setItem('session_referrer', session_referrer)
  }

  let session_referring_domain = '$direct'
  try {
    if (session_referrer) session_referring_domain = new URL(session_referrer).hostname
  } catch (e) {
    // Invalid URL, fallback to $direct
  }

  register({
    session_referrer: session_referrer || '$direct',
    session_referring_domain,
  })
}

const setPricingVersionSuperProps = () => {
  // Get pricing_version from cookie and register it as super property
  const pricingVersion = cookie.getValue('aha_pricing_version')

  if (typeof pricingVersion === 'string' && pricingVersion) {
    register({
      pricing_version: pricingVersion,
    })
  }
}

const setHomePageVariantSuperProps = () => {
  // Get pricing_version from cookie and register it as super property
  const homePageVariant = cookie.getValue('home_page_variant')

  if (typeof homePageVariant === 'string' && homePageVariant) {
    register({
      home_page_variant: homePageVariant,
    })
  }
}

const init = (additionalOptions = {}) => {
  try {
    if (mixpanelAvailable) {
      mixpanel.init(config.mixpanel.projectToken, {
        debug: config.env !== 'production' || localStorage.getItem('mt-enabled') === 'true',
        api_host: config.mixpanel.proxyUrl,
        record_sessions_percent: 1,
        record_max_ms: 10000,
        ...additionalOptions,
      })
      isInitialized = true
      try {
        setUtmAndReferrerSuperProps()
        setPricingVersionSuperProps()
        setHomePageVariantSuperProps()
      } catch (error) {
        Sentry.captureException(error)
      }
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}
const start_session_recording = () => {
  try {
    if (mixpanelAvailable) {
      mixpanel.start_session_recording()
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}
const stop_session_recording = () => {
  try {
    if (mixpanelAvailable) {
      mixpanel.stop_session_recording()
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const track = (eventName, eventProps) => {
  if (!mixpanelAvailable || isAdmin() || !eventName) return

  const trackAction = () => {
    try {
      mixpanel.track(eventName, eventProps)
    } catch {
      // do nothing
    }
  }

  const currentRouteName = store.state.route?.name
  const isPublicRoute = PUBLIC_ROUTES.includes(currentRouteName)

  if (isPublicRoute) {
    if (store.state.featureToggles?.hasLoadedAnonymousFlag) {
      trackAction()
    } else {
      pushToQueue('mixpanel_anonymous', trackAction)
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (store.state.featureToggles?.hasLoadedAuthFlag) {
      trackAction()
    } else {
      pushToQueue('mixpanel_auth', trackAction)
    }
  }
}

const trackForLoginUser = (eventName, eventProps) => {
  if (!mixpanelAvailable || !isUserIdentified || !eventName) return

  const trackAction = () => {
    try {
      mixpanel.track(eventName, eventProps)
    } catch {
      // do nothing
    }
  }

  // trackForLoginUser is always for auth phase
  if (store.state.featureToggles?.hasLoadedAuthFlag) {
    trackAction()
  } else {
    pushToQueue('mixpanel_auth', trackAction)
  }
}

const timeEvent = (eventName) => {
  if (mixpanelAvailable && eventName) {
    try {
      mixpanel.time_event(eventName)
    } catch {
      // do nothing
    }
  }
}

const immutableUserProperties = (userData) => {
  const props = {
    abTestGroup: userData.id % 4,
    $created: userData.createdAt,
    signUpSource: userData.source,
    referralUserId: userData.referralUserId,
  }

  if (userData.country) {
    props.$country_code = userData.country
  }
  if (userData.city) {
    props.$city = userData.city
  }
  return props
}

const mutableUserProperties = (userData) => {
  const props = {
    $email: userData.email,
    $first_name: userData.firstName,
    $last_name: userData.lastName,
    useAhaSlidesFor: userData.useAhaSlidesFor,
    userProfession: userData.userProfession,
    plan: userData.plan,
    firstEventStartedAt: userData.firstEventStartedAt,
    lastEventStartedAt: userData.lastEventStartedAt,
    teamRole: userData.accountRole,
    isTeamUser: !!userData.accountId,
    stripeId: userData.stripeCustomerId,
  }
  if (userData.abTest) {
    props.abTest = userData.abTest
  }
  if (userData.firstPage) {
    props.firstPage = userData.firstPage
  }
  if (userData.personalInformation && userData.personalInformation.presenterLanguage) {
    props.presenterLanguage = userData.personalInformation.presenterLanguage
  }
  return props
}

const identify = (user) => {
  if (mixpanelAvailable && user && !isAdmin()) {
    try {
      if (!localGetters.canUseMixpanelLocalStorage()) {
        // alias is a legacy method. Identify is supposed to do the joining of distinct_id and user_id
        // into a single cluster.
        // take this opportunity to remove alias
        if (user && user.id && String(mixpanel.get_distinct_id()) !== String(user.id)) {
          mixpanel.alias(user.id)
        }
      }
      mixpanel.identify(user.id)
      const isTeamUser = !!user.accountId

      user.isTeamUser = isTeamUser
      user.useAhaSlidesFor = lodashGet(user, 'personalInformation.useAhaSlidesFor', null)
      user.userProfession = lodashGet(user, 'personalInformation.userProfession', null)

      // Set user profile properties
      mixpanel.people.set_once({
        $distinct_id: user.id,
        ...immutableUserProperties(user),
      })
      mixpanel.people.set({
        ...mutableUserProperties(user),
      })

      // Includes user properties in all events
      mixpanel.register_once(immutableUserProperties(user))
      mixpanel.register(mutableUserProperties(user))
      isUserIdentified = true
    } catch (e) {
      // do nothing if mixpanel can not identify user
    }
  }
}

const setTeamProps = (team) => {
  if (mixpanelAvailable && team && !isAdmin()) {
    try {
      mixpanel.register({
        teamPlan: team.plan || 'free',
        isSelfCreatedTeam: team.createdFrom === 'presenter',
      })
    } catch (e) {
      // do nothing if mixpanel can not identify user
    }
  }
}

const getPresentationContext = (presentation) => {
  if (!presentation) return {}
  // We only need the following fields for presentation context, dont need all fields
  const lastSlide = presentation.slides && presentation.slides.length ?
    presentation.slides[presentation.slides.length - 1] :
    null
  return {
    presentationId: presentation.id,
    accessCode: presentation.accessCode,
    uniqueAccessCode: presentation.uniqueAccessCode,
    areSlideOptionsShuffling: presentation.areSlideOptionsShuffling,
    isUsingAudienceAdmission: !!presentation.audienceAdmission,
    audiencePacing: presentation.audiencePacing,
    createdAt: presentation.createdAt,
    modifiedAt: presentation.modifiedAt,
    updatedAt: presentation.updatedAt,
    enableCopySlideNote: presentation.enableCopySlideNote,
    enableQuizCountdown: presentation.enableQuizCountdown,
    presentationLanguage: presentation.language,
    filteringProfanity: presentation.filteringProfanity,
    isEnableChat: presentation.isEnableChat,
    isEnableQuizMusic: presentation.isEnableQuizMusic,
    isModerationMode: presentation.isModerationMode,
    isReactionEnabled: presentation.isReactionEnabled,
    presenting: presentation.presenting,
    privateMode: presentation.privateMode,
    qnaAllSlide: presentation.qnaAllSlide,
    qnaAnonymous: presentation.qnaAnonymous,
    qnaAudienceShowAll: presentation.qnaAudienceShowAll,
    showAhaSlidesCTA: presentation.showAhaSlidesCTA,
    showHyperLink: presentation.showHyperLink,
    slideCount: presentation.slideCount,
    sourcePresentation: presentation.sourcePresentation,
    manualRevealCorrectAnswers: presentation.manualRevealCorrectAnswers,
    tags: presentation.tags,
    teamPlay: presentation.teamPlay,
    teamCount: presentation.teamCount,
    teamSize: presentation.teamSize,
    teamScoringRule: presentation.teamScoringRule,
    whiteLabelPresenterDomain: presentation.whiteLabelPresenterDomain,
    whiteLabelLink: presentation.whiteLabelLink,
    isPublicSearch: presentation.isPublicSearch,
    lastSlideType: lastSlide ? lastSlide.type : undefined,
  }
}

const getPlanContext = (plan) => {
  if (!plan) return {}
  return {
    planType: plan.type,
    planGroup: plan.group,
    planBillingCycle: plan.billingCycle,
  }
}

const unregisterPresentationContext = () => {
  const presentationContextProps = [
    'presentationId',
    'accessCode',
    'uniqueAccessCode',
    'areSlideOptionsShuffling',
    'isUsingAudienceAdmission',
    'audiencePacing',
    'createdAt',
    'modifiedAt',
    'updatedAt',
    'enableCopySlideNote',
    'enableQuizCountdown',
    'presentationLanguage',
    'filteringProfanity',
    'isEnableChat',
    'isEnableQuizMusic',
    'isModerationMode',
    'isReactionEnabled',
    'presenting',
    'privateMode',
    'qnaAllSlide',
    'qnaAnonymous',
    'qnaAudienceShowAll',
    'showAhaSlidesCTA',
    'showHyperLink',
    'slideCount',
    'sourcePresentation',
    'tags',
    'teamPlay',
    'teamCount',
    'teamSize',
    'teamScoringRule',
    'whiteLabelPresenterDomain',
    'whiteLabelLink',
  ]

  for (let i = 0; i < presentationContextProps.length; i++) {
    unregister(presentationContextProps[i])
  }
}

const getHostingEventInsertId = (userId, presentationId, eventTime, isUserFirstEvent) => {
  const hour = eventTime.getHours()
  if (isUserFirstEvent) {
    return `aha-${userId}-first`
  }
  // $insert_id max len = 36 chars, so keep it short
  return `aha-${presentationId}-${hour}`
}

const setHostingEventTime = (presentationId, eventTime) => {
  if (!presentationId || !eventTime) return
  localStorage.setItem(`host-event-time-${presentationId}`, eventTime)
}
const getHostingEventTime = (presentationId) => {
  if (!presentationId) return null
  return +localStorage.getItem(`host-event-time-${presentationId}`) || null
}

const trackHostingEvent = async ({
  presentation, onlineCount, maxOnlineCount, routeName,
}) => {
  // Record mixpanel event for Hosting event
  // Cần phân biệt việc click nút Present với actual Hosting event
  // Để được coi là hosting event, thì event đó phải có số online count > 3
  // và tạm coi một event diễn ra trong 1h, trong timebox 1h này chỉ tính duy nhất 1 lần
  // việc start hosting event
  // Chỉ track khi onlineCount có xu hướng đi lên
  const START_HOSTING_EVENT = 'Start hosting event'
  const user = presentation.TakenOverByUser || presentation.User
  if (user && onlineCount > 3 &&
    (!maxOnlineCount || onlineCount > maxOnlineCount)
  ) {
    const eventTime = new Date()
    // This issue happened long ago due to we store the moment object inside vuex state.
    // then the mixpanel get the data from it. (inside getCurrentUserAction)
    // Solution: turn it into isostring (still need to put a flag because I’ve changed the data type)
    const canUseIsoString = store.getters['featureToggles/canUseISOStringForEventStartedAt']
    const eventStartedAt = canUseIsoString
      ? moment(eventTime).utc().toISOString()
      : moment(eventTime).utc()
    // For old users that don't have the 'firstEventStartedAt' field,
    // we should count the hosted presentations that they already have
    // count it on App.vue
    const isUserFirstEvent = !user.firstEventStartedAt && !store.state.userHasHostedEvents
    const presentationTracker = new PresentationMixpanelTracker(presentation, routeName, store.state.currentUser)
    if (isUserFirstEvent) {
      await UserApi.patchUserById(user.id, {
        firstEventStartedAt: eventStartedAt,
        lastEventStartedAt: eventStartedAt,
      })
      store.state.currentUser.firstEventStartedAt = eventStartedAt
      store.state.currentUser.lastEventStartedAt = eventStartedAt
      const presentationOwnerId = presentation.userId
      let isPresentationOwner = false
      if (presentation.userId === store.state.currentUser.id) {
        isPresentationOwner = true
      }
      let presentationOwnerCurrentPlan = null
      if (presentation.User) {
        presentationOwnerCurrentPlan = presentation.User.plan
      }
      track(START_HOSTING_EVENT, {
        $insert_id: getHostingEventInsertId(
          user.id,
          presentation.id,
          eventTime,
          true,
        ),
        isUserFirstEvent,
        presentationOwnerId,
        isPresentationOwner,
        presentationOwnerCurrentPlan,
        ...presentationTracker.getCustomPropsTrackStartPresenting(),
      })

      // AHA-21083 Add trigger for GTM
      window.dataLayer.push({
        event: START_HOSTING_EVENT,
        isUserFirstEvent,
      })
    } else {
      const lastHostEventTime = getHostingEventTime(presentation.id)
      const ONE_HOUR = 3600e3
      const now = new Date().getTime()
      if (!lastHostEventTime || (now > lastHostEventTime + ONE_HOUR)) {
        await UserApi.patchUserById(user.id, {
          lastEventStartedAt: eventStartedAt,
        })

        const presentationOwnerId = presentation.userId
        let isPresentationOwner = false
        if (presentation.userId === store.state.currentUser.id) {
          isPresentationOwner = true
        }
        let presentationOwnerCurrentPlan = null
        if (presentation.User) {
          presentationOwnerCurrentPlan = presentation.User.plan
        }
        track(START_HOSTING_EVENT, {
          $insert_id: getHostingEventInsertId(
            user.id,
            presentation.id,
            eventTime,
            false,
          ),
          presentationOwnerId,
          isPresentationOwner,
          presentationOwnerCurrentPlan,
          ...presentationTracker.getCustomPropsTrackStartPresenting(),
        })

        // AHA-21083 Add trigger for GTM
        window.dataLayer.push({
          event: START_HOSTING_EVENT,
        })

        setHostingEventTime(presentation.id, now)
      }
    }
    if (store.state.isReferPublicTemplate) {
      track(START_HOSTING_EVENT, { eventCategory: 'Public template > Demo mode' })
    }
  }
}

const trackCharge = (amount, properties = {}) => {
  if (mixpanelAvailable) {
    const { discountWithCredit, discountWithCoupon, totalAmount } = properties
    const convertProperties = {
      discountWithCredit: discountWithCredit || 0,
      discountWithCoupon: discountWithCoupon || 0,
      totalAmount: totalAmount || 0,
      customerLifetimeValue: amount,
    }
    mixpanel.people.track_charge(amount, convertProperties)
    mixpanel.people.increment(convertProperties)
  }
}

const getChargeContext = (data) => {
  let {
    paidAmount, credit, coupon,
  } = data

  paidAmount = parseFloat(paidAmount)
  credit = Math.abs(credit) || 0
  coupon = Math.abs(coupon) || 0
  const totalAmount = parseFloat(Util.roundNumber(paidAmount + credit + coupon))

  return {
    discountWithCredit: credit,
    discountWithCoupon: coupon,
    paidAmount,
    totalAmount,
  }
}

const trackTimeEvent = (eventName) => {
  try {
    mixpanel.time_event(eventName)
  } catch (error) {
    Sentry.captureException(error)
  }
}

const reset = () => {
  try {
    if (mixpanelAvailable && isInitialized) {
      mixpanel.reset()
      isUserIdentified = false
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const getProperties = () => {
  try {
    if (mixpanelAvailable && isInitialized) {
      const mixpanelProps = { distinct_id: mixpanel.get_distinct_id() }
      const keys = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        '$device_id',
        '$referrer',
        '$referring_domain',
        'session_referrer',
        'pricing_version',
      ]
      keys.forEach((key) => {
        if (mixpanel.get_property(key)) {
          mixpanelProps[key] = mixpanel.get_property(key)
        }
      })
      return mixpanelProps
    }
  } catch (error) {
    Sentry.captureException(error)
    return {}
  }
}

const isReady = () => {
  return isInitialized
}

const isLoggedInUserIdentified = () => {
  return isUserIdentified
}

export default {
  init,
  track,
  register,
  identify,
  getPresentationContext,
  getPlanContext,
  unregisterPresentationContext,
  mixpanelAvailable,
  unregister,
  trackHostingEvent,
  trackCharge,
  getChargeContext,
  trackTimeEvent,
  reset,
  setTeamProps,
  start_session_recording,
  stop_session_recording,
  getProperties,
  isReady,
  timeEvent,
  isLoggedInUserIdentified,
  trackForLoginUser,
}
